import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {isLoaded as isLoadedPackage, PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {Loaded} from 'Core/models/Async';
import {
  isLoaded,
  SalaryDeductionOccupationClassInfo,
  SalaryDeductionOccupationClassModel,
  UnassignedSalaryDeductionOccupationClassModel,
} from '../SalaryDeductionOccupationClassModel';

type DisabilityProps = {
  plan: StringUnion;
  issueType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  monthlySalary: StringUnion;
  type: StringUnion;
  accident: StringUnion;
  sickness: StringUnion;
  packageSource: StringUnion;
  packageSourceList: PackageModel;
  situsState: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  monthlyBenefit: StringUnion;
  benefitPeriod: StringUnion;
  mentalNervousExclusion: SalaryDeductionOccupationClassModel;
  isGvdiPortable: StringUnion;
  gcroprRider: boolean;
  oadr1Rider: boolean;
  fmdr1Rider: boolean;
  ibdr1Rider: boolean;
  sadrRider: boolean;
  jStamp: boolean;
  occClass: SalaryDeductionOccupationClassModel
};

export class InvalidGroupDisability {
  public readonly key = 'InvalidGroupDisability';

  constructor(
    public readonly plan: StringUnion,
    public readonly issueType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly type: StringUnion,
    public readonly accident: StringUnion,
    public readonly sickness: StringUnion,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel,
    public readonly situsState: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly monthlyBenefit: StringUnion,
    public readonly benefitPeriod: StringUnion,
    public readonly mentalNervousExclusion: SalaryDeductionOccupationClassModel,
    public readonly isGvdiPortable: StringUnion,
    public readonly gcroprRider: boolean,
    public readonly oadr1Rider: boolean,
    public readonly fmdr1Rider: boolean,
    public readonly ibdr1Rider: boolean,
    public readonly sadrRider: boolean,
    public readonly jStamp: boolean,
    public readonly occClass: SalaryDeductionOccupationClassModel
  ) {}
}

export class ValidGroupDisability {
  public static createApp({
    plan,
    issueType,
    premium,
    calculatedPremium,
    cwa,
    monthlySalary,
    type,
    accident,
    sickness,
    packageSource,
    packageSourceList,
    situsState,
    section125,
    replacement,
    policyNumber,
    monthlyBenefit,
    benefitPeriod,
    mentalNervousExclusion,
    isGvdiPortable,
    gcroprRider,
    oadr1Rider,
    fmdr1Rider,
    ibdr1Rider,
    sadrRider,
    jStamp,
    occClass
  }: DisabilityProps) {
    return this.create(
      plan,
      issueType,
      premium,
      calculatedPremium,
      cwa,
      monthlySalary,
      type,
      accident,
      sickness,
      packageSource,
      packageSourceList,
      situsState,
      section125,
      replacement,
      policyNumber,
      monthlyBenefit,
      benefitPeriod,
      mentalNervousExclusion,
      isGvdiPortable,
      gcroprRider,
      oadr1Rider,
      fmdr1Rider,
      ibdr1Rider,
      sadrRider,
      jStamp,
      occClass
    );
  }

  public static create(
    plan: StringUnion,
    issueType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    monthlySalary: StringUnion,
    type: StringUnion,
    accident: StringUnion,
    sickness: StringUnion,
    packageSource: StringUnion,
    packageSourceList: PackageModel,
    situsState: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    monthlyBenefit: StringUnion,
    benefitPeriod: StringUnion,
    mentalNervousExclusion: SalaryDeductionOccupationClassModel,
    isGvdiPortable: StringUnion,
    gcroprRider: boolean,
    oadr1Rider: boolean,
    fmdr1Rider: boolean,
    ibdr1Rider: boolean,
    sadrRider: boolean,
    jStamp: boolean,
    occClass: SalaryDeductionOccupationClassModel
  ) {
    return isNonEmptyString(issueType) && 
    isNonEmptyString(type) && 
    isLoadedPackage(packageSourceList) && 
    isLoaded(occClass) &&
    isLoaded(mentalNervousExclusion)
      ? new ValidGroupDisability(
          plan,
          issueType,
          premium,
          calculatedPremium,
          cwa,
          monthlySalary,
          type,
          accident,
          sickness,
          packageSource,
          packageSourceList,
          situsState,
          section125,
          replacement,
          policyNumber,
          monthlyBenefit,
          benefitPeriod,
          mentalNervousExclusion,
          isGvdiPortable,
          gcroprRider,
          oadr1Rider,
          fmdr1Rider,
          ibdr1Rider,
          sadrRider,
          jStamp,
          occClass
        )
      : new InvalidGroupDisability(
          plan,
          issueType,
          premium,
          calculatedPremium,
          cwa,
          monthlySalary,
          type,
          accident,
          sickness,
          packageSource,
          packageSourceList,
          situsState,
          section125,
          replacement,
          policyNumber,
          monthlyBenefit,
          benefitPeriod,
          mentalNervousExclusion,
          isGvdiPortable,
          gcroprRider,
          oadr1Rider,
          fmdr1Rider,
          ibdr1Rider,
          sadrRider,
          jStamp,
          occClass
        );
  }

  public readonly key = 'ValidGroupDisability';
  constructor(
    public readonly plan: StringUnion,
    public readonly issueType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly monthlySalary: StringUnion,
    public readonly type: StringUnion,
    public readonly accident: StringUnion,
    public readonly sickness: StringUnion,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel,
    public readonly situsState: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly monthlyBenefit: StringUnion,
    public readonly benefitPeriod: StringUnion,
    public readonly mentalNervousExclusion: Loaded<SalaryDeductionOccupationClassInfo>,
    public readonly isGvdiPortable: StringUnion,
    public readonly gcroprRider: boolean,
    public readonly oadr1Rider: boolean,
    public readonly fmdr1Rider: boolean,
    public readonly ibdr1Rider: boolean,
    public readonly sadrRider: boolean,
    public readonly jStamp: boolean,
    public readonly occClass: Loaded<SalaryDeductionOccupationClassInfo>
  ) {}
}

export type GroupDisability = InvalidGroupDisability | ValidGroupDisability;

export type GroupDisabilityOptions<TReturn> = ToPartialMatchOptions<GroupDisability, 'key', TReturn>;

export const matchGroupDisability = <TReturn>(options: GroupDisabilityOptions<TReturn>) => (
  app: GroupDisability
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupDisability':
      return options.InvalidGroupDisability ? options.InvalidGroupDisability(app) : options.default(app);
    case 'ValidGroupDisability':
      return options.ValidGroupDisability ? options.ValidGroupDisability(app) : options.default(app);
  }
};

export const plans = [
  { name: 'GVDIP', displayName: 'GVDIP' }
];

export const EmptyGroupDisability = new InvalidGroupDisability(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new UnassignedPackageModel(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  false,
  false,
  false,
  new UnassignedSalaryDeductionOccupationClassModel()
);

export const changePlan = (plan: string) => (app: GroupDisability) =>
ValidGroupDisability.createApp({...app, plan: NonEmptyString.create(plan)});

export const changeMentalNervousExclusion = (mentalNervousExclusion: SalaryDeductionOccupationClassModel) => (app: GroupDisability) =>
ValidGroupDisability.createApp({...app, mentalNervousExclusion});
export const changeSaldOccClass = (occClass: SalaryDeductionOccupationClassModel) => (app: GroupDisability) =>
ValidGroupDisability.createApp({...app, occClass});
export const changeIssueType = (issueType: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, issueType: NonEmptyString.create(issueType)});
export const changeGvdipPortability = (isGvdiPortable: string) => (app: GroupDisability) =>
ValidGroupDisability.createApp({...app, isGvdiPortable: NonEmptyString.create(isGvdiPortable)});
export const changePremium = (premium: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, premium: NonEmptyString.create(premium)});
  export const changeCalculatedPremium = (calculatedPremium : string) => (app : GroupDisability) => 
  ValidGroupDisability.createApp({...app, calculatedPremium : NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeMonthlySalary = (monthlySalary: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, monthlySalary: NonEmptyString.create(monthlySalary)});
export const changeType = (type: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, type: NonEmptyString.create(type)});
export const changeAccident = (accident: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, accident: NonEmptyString.create(accident)});
export const changeSickness = (sickness: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, sickness: NonEmptyString.create(sickness)});
export const changePackageSource = (packageSource: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, packageSource: NonEmptyString.create(packageSource)});
export const changeBenefitMonthly = (monthlyBenefit: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, monthlyBenefit: NonEmptyString.create(monthlyBenefit)});
export const changeSitusState = (situsState: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeReplacement = (replacement: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});
export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});
export const changeBenefitPeriod = (benefitPeriod: string) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, benefitPeriod: NonEmptyString.create(benefitPeriod)});
export const changeSection125 = (section: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, section125: section});
export const changeGcropr = (gcropr: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, gcroprRider: gcropr});
export const changeFmdr1 = (fmdr1: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, fmdr1Rider: fmdr1});
export const changeOadr1 = (oadr1: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, oadr1Rider: oadr1});
export const changeIbdr1 = (ibdr1: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, ibdr1Rider: ibdr1});
export const changeSadr = (sadr: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, sadrRider: sadr});
export const changeJStamp = (jStampVal: boolean) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, jStamp: jStampVal});
export const changePackageSourceList = (packageSourceList: PackageModel) => (app: GroupDisability) =>
  ValidGroupDisability.createApp({...app, packageSourceList});
